/**
 * Carry-over expiry: the Save dialog (DESIGN-rollover.md, "Carry-over expiry", 09/10/2026).
 *
 * Rob: when someone changes the expiry setting, "it tells them who it will affect and gives
 * them the option". It opens only when the change reaches people who already hold
 * carried-over days this leave year (GET /settings/carry-over-expiry/impact); otherwise
 * Save just saves and the new setting starts with the next carry-over.
 *
 * ⚠️ THE DEFAULT IS DECISION c, CHOSEN BY THE SERVER (`recommended_apply_now`): more time is
 * ticked to apply now; less time is ticked to wait for next year. The harsh choice is never
 * pre-selected, and when the new date has already passed it says in red exactly how much
 * goes, from whom, today.
 *
 * ⚠️ EVERY FIGURE IN THE PERSON'S OWN UNIT (leaveUnits.js — "the unit is the OWNING
 * EMPLOYEE's"), so the red line has no combined total: hours and days don't add.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { formatDateGB } from '../lib/dates';
import { formatLeave } from '../lib/leaveUnits';

const amount = (p, days, minutes) => formatLeave(days, { unit: p.display_unit, minutes });
const someLost = (p) => (p.lost_today_days || 0) > 0.0001 || (p.lost_today_minutes || 0) > 0;
const never = (iso) => (iso ? formatDateGB(iso) : 'never');

function Option({ selected, danger, onSelect, title, children, recommended, testid }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testid}
      className={`w-full text-left flex gap-3 rounded-lg border p-3 transition-colors ${
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-secondary/40'}`}
    >
      <span className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
        danger ? 'border-red-500' : 'border-primary'}`}>
        {selected && <span className={`w-2 h-2 rounded-full ${danger ? 'bg-red-500' : 'bg-primary'}`} />}
      </span>
      <span>
        <span className="font-medium text-sm">
          {title}
          {recommended && (
            <span className="ml-2 text-[11px] font-semibold text-green-700 bg-green-100 dark:bg-green-900/40 dark:text-green-300 rounded-full px-2 py-0.5">
              Recommended
            </span>
          )}
        </span>
        <span className="block text-xs text-muted-foreground mt-0.5">{children}</span>
      </span>
    </button>
  );
}

export default function CarryExpiryDialog({ impact, saving, onCancel, onConfirm }) {
  const [applyNow, setApplyNow] = useState(false);
  useEffect(() => {
    if (impact) setApplyNow(!!impact.recommended_apply_now);
  }, [impact]);
  if (!impact) return null;

  const more = impact.direction === 'more';
  const people = impact.people || [];
  const current = impact.use_by;
  const next = impact.new_use_by;
  const passed = !!impact.new_use_by_passed;
  const losing = people.filter(someLost);
  const n = people.length;
  const plural = n === 1 ? 'person' : 'people';

  return (
    <Dialog open={!!impact} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="sm:max-w-[560px]" data-testid="carry-expiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {more ? 'Give people more time?' : 'This gives people less time'}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {n} {plural} carried days over into this leave year.{' '}
          {current
            ? <>Their use-by date is <strong className="text-foreground">{formatDateGB(current)}</strong>. </>
            : <>Their carried-over days don't expire at the moment. </>}
          {next
            ? <>With the new setting it would be <strong className="text-foreground">{formatDateGB(next)}</strong>{passed ? <>, which has <strong className="text-foreground">already passed</strong></> : null}.</>
            : <>With the new setting they would never expire.</>}
        </p>

        <div className="max-h-64 overflow-y-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground border-b border-border">
                <th className="text-left font-medium px-3 py-2">Person</th>
                <th className="text-right font-medium px-3 py-2">Carried over</th>
                {passed
                  ? <>
                      <th className="text-right font-medium px-3 py-2">Taken by {formatDateGB(next).slice(0, 5)}</th>
                      <th className="text-right font-medium px-3 py-2">Lost today</th>
                    </>
                  : <th className="text-right font-medium px-3 py-2">Not booked yet</th>}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => (
                <tr key={p.user_id} className="border-b border-border/50 last:border-0">
                  <td className="px-3 py-2">{p.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{amount(p, p.carried_days, p.carried_minutes)}</td>
                  {passed
                    ? <>
                        <td className="px-3 py-2 text-right tabular-nums">{amount(p, p.taken_by_new_days, p.taken_by_new_minutes)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${someLost(p) ? 'text-red-600 font-semibold' : ''}`}>
                          {amount(p, p.lost_today_days, p.lost_today_minutes)}
                        </td>
                      </>
                    : <td className="px-3 py-2 text-right tabular-nums">{amount(p, p.not_booked_days, p.not_booked_minutes)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-2">
          {more ? (
            <>
              <Option selected={applyNow} onSelect={() => setApplyNow(true)} recommended
                testid="carry-expiry-apply-now"
                title={next ? `Move their date to ${formatDateGB(next)} now` : 'Let their days never expire, from now'}>
                They get the extra time this leave year.
              </Option>
              <Option selected={!applyNow} onSelect={() => setApplyNow(false)}
                testid="carry-expiry-keep"
                title={`Keep ${never(current)} for them`}>
                The new setting starts with next year's carry-over.
              </Option>
            </>
          ) : (
            <>
              <Option selected={!applyNow} onSelect={() => setApplyNow(false)} recommended
                testid="carry-expiry-keep"
                title={current ? `Keep ${formatDateGB(current)} for them` : 'Keep their days from expiring this year'}>
                The new setting starts with next year's carry-over. Nobody loses anything today.
              </Option>
              <Option selected={applyNow} danger onSelect={() => setApplyNow(true)}
                testid="carry-expiry-apply-now"
                title={`Move their date to ${never(next)} now`}>
                {passed
                  ? (losing.length
                      ? <span className="text-red-600 font-medium">Carried-over leave is removed today from {losing.length} {losing.length === 1 ? 'person' : 'people'}: {losing.map((p) => p.name).join(', ')}. This can't be undone from here.</span>
                      : <>That date has passed, but everyone has already taken their carried-over days, so nobody loses anything.</>)
                  : <>They'll have until {never(next)} to use them, and get a reminder email the month before.</>}
              </Option>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button type="button" className="btn-primary" onClick={() => onConfirm(applyNow)} disabled={saving}
            data-testid="carry-expiry-save">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
